import { Card, Colors, PageContainer, Text } from '@amelio/ui-library';
import { useCallback, useEffect, useState } from 'react';

import { COMPENSATION_ACTION_BAR_Z_INDEX } from '../compensationStyles';
import { AdjustmentsSection } from './AdjustmentsSection';
import { AudienceSection } from './AudienceSection';
import { BudgetSection } from './BudgetSection';
import { CompensationSaveBar } from './CompensationSaveBar';
import { DeleteEnvelopeDialog } from './DeleteEnvelopeDialog';
import { EnvelopeActionBar } from './EnvelopeActionBar';
import { HistoryModal } from './HistoryModal';
import { ImportSalariesModal } from './ImportSalariesModal';
import { ReportNavigation } from './ReportNavigation';
import { useCompensationPreview } from '../hooks/useCompensationPreview';
import { useEnvelopeActions } from '../hooks/useEnvelopeActions';
import { useEnvelopeDraft } from '../hooks/useEnvelopeDraft';
import { isMissingAudienceCriterion } from '../utils/audienceRequest';

import type { ReportNavigationProps } from './ReportNavigation';
import type {
    CompensationAudience,
    CompensationEnvelope,
    CompensationRecognizedSalary,
} from '@/types/compensation';

import { useGetCompanyInformation } from '@/api/directory';
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog/UnsavedChangesDialog';
import { useAmelioTranslations } from '@/hooks';
import { showSuccessToast } from '@/plugins/Toast';

interface EnvelopeWorkspaceProps {
    envelope: CompensationEnvelope;
    navigation: ReportNavigationProps;
    onDeleted: () => void;
    onReload: () => void;
}

/**
 * The open envelope: the audience it covers, the budget it holds, the strategy that spreads it and
 * the person-by-person adjustments. Everything on screen is a local draft until "Sauvegarder", and
 * every figure is the backend's computation of that draft.
 */
export const EnvelopeWorkspace = ({
    envelope,
    navigation,
    onDeleted,
    onReload,
}: EnvelopeWorkspaceProps) => {
    const { t } = useAmelioTranslations();

    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

    const { data: company, isError: hasCompanyFailed, refetch } = useGetCompanyInformation();
    const { draft, dispatch, isDirty, draftRequest } = useEnvelopeDraft(envelope);
    const { computed, hasPreviewFailed } = useCompensationPreview({
        envelopeId: envelope.id,
        draftRequest: isDirty ? draftRequest : undefined,
        savedView: envelope.computed,
    });
    const actions = useEnvelopeActions({ envelope, onDeleted, onReload });

    const isEditable = envelope.status !== 'Closed';

    // The attribute picker reports its selection from an effect that depends on this callback's
    // identity: an inline arrow would report, and dispatch, on every render.
    const handleAudienceChange = useCallback(
        (audience: CompensationAudience) => dispatch({ type: 'setAudience', audience }),
        [dispatch],
    );

    // The browser's own guard, for the one exit this page cannot render a dialog over.
    useEffect(() => {
        if (!isDirty) {
            return;
        }

        const warnBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();

        window.addEventListener('beforeunload', warnBeforeUnload);
        return () => window.removeEventListener('beforeunload', warnBeforeUnload);
    }, [isDirty]);

    const guardNavigation = (navigate: () => void) => {
        if (isDirty) {
            setPendingNavigation(() => navigate);
            return;
        }

        navigate();
    };

    const handleApplySalaries = (salaries: CompensationRecognizedSalary[]) => {
        dispatch({
            type: 'applySalaries',
            salaries: salaries.map(({ employeeId, amount }) => ({ employeeId, salary: amount })),
        });
        showSuccessToast({ message: t('compensation.import.done', { count: salaries.length }) });
    };

    return (
        <PageContainer>
            <div className={'flex flex-col gap-6'}>
                <ReportNavigation
                    {...navigation}
                    guardNavigation={guardNavigation}
                />

                <Card
                    variant={'glass'}
                    padding={'sm'}
                    borderRadius={'3xl'}
                    zIndex={COMPENSATION_ACTION_BAR_Z_INDEX}
                    className={'sticky max-md:top-[48px] md:top-[96px]'}
                    dataTestId={'compensation-action-bar'}
                >
                    <div className={'flex flex-wrap items-center gap-[12px]'}>
                        {isEditable ? (
                            <CompensationSaveBar
                                isDirty={isDirty}
                                isSaving={actions.isSaving}
                                isSaveBlocked={isMissingAudienceCriterion(draft.audience)}
                                onDiscard={() => dispatch({ type: 'reset' })}
                                onSave={() => void actions.save(draftRequest)}
                            />
                        ) : null}

                        {/* Inside the bar, whose height never moves, so the page does not shift. */}
                        <div
                            role={'alert'}
                            data-testid={'compensation-preview-failed'}
                        >
                            {hasPreviewFailed ? (
                                <Text
                                    color={Colors.ERROR_800}
                                    className={'text-[13px]'}
                                >
                                    {t('compensation.errors.previewFailed')}
                                </Text>
                            ) : null}
                        </div>

                        <EnvelopeActionBar
                            status={envelope.status}
                            onOpenHistory={() => setIsHistoryOpen(true)}
                            onToggleStatus={() => void actions.toggleStatus()}
                            isTogglingStatus={actions.isTogglingStatus}
                        />
                    </div>
                </Card>

                <AudienceSection
                    name={draft.name}
                    audience={draft.audience}
                    isCompanyWide={envelope.isCompanyWide}
                    computed={computed}
                    company={company}
                    hasCompanyFailed={hasCompanyFailed}
                    onRetryCompany={() => void refetch()}
                    canEdit={isEditable}
                    nameError={actions.nameError}
                    onNameChange={(name) => dispatch({ type: 'setName', name })}
                    onAudienceChange={handleAudienceChange}
                    onDelete={() => setIsDeleteOpen(true)}
                />

                <BudgetSection
                    draft={draft}
                    dispatch={dispatch}
                    computed={computed}
                    levels={envelope.levels}
                    canEdit={isEditable}
                    onImportSalaries={
                        envelope.canRecordSalaries ? () => setIsImportOpen(true) : undefined
                    }
                />

                {computed && computed.lines.length > 0 ? (
                    <AdjustmentsSection
                        computed={computed}
                        draftLines={draft.lines}
                        levels={envelope.levels}
                        canEdit={isEditable}
                        canRecordSalaries={envelope.canRecordSalaries}
                        onSalaryChange={({ employeeId, salary }) =>
                            dispatch({ type: 'setLineSalary', employeeId, salary })
                        }
                        onPercentChange={({ employeeId, percent }) =>
                            dispatch({ type: 'setLinePercent', employeeId, percent })
                        }
                        onLockChange={({ employeeId, lockedAmount }) =>
                            dispatch(
                                lockedAmount === null
                                    ? { type: 'unlockLine', employeeId }
                                    : { type: 'lockLine', employeeId, amount: lockedAmount },
                            )
                        }
                        onNoteChange={({ employeeId, note }) =>
                            dispatch({ type: 'setLineNote', employeeId, note })
                        }
                        onExport={() => void actions.exportSalaries()}
                        isExporting={actions.isExporting}
                    />
                ) : null}
            </div>

            <HistoryModal
                isOpen={isHistoryOpen}
                onClose={() => setIsHistoryOpen(false)}
                envelope={envelope}
                canRestore={isEditable}
            />

            <ImportSalariesModal
                isOpen={isImportOpen}
                onClose={() => setIsImportOpen(false)}
                envelopeId={envelope.id}
                lines={computed?.lines ?? []}
                onApply={handleApplySalaries}
            />

            <DeleteEnvelopeDialog
                isOpen={isDeleteOpen}
                isLastEnvelope={(navigation.overview?.envelopes.length ?? 0) <= 1}
                isDeleting={actions.isDeleting}
                onConfirm={() => void actions.remove()}
                onCancel={() => setIsDeleteOpen(false)}
            />

            <UnsavedChangesDialog
                isOpen={pendingNavigation !== null}
                onConfirm={() => {
                    dispatch({ type: 'reset' });
                    pendingNavigation?.();
                    setPendingNavigation(null);
                }}
                onCancel={() => setPendingNavigation(null)}
            />
        </PageContainer>
    );
};
